import { useMemo, type FC } from 'react';
import { PapiBadge, PapiUser } from '@wix/da-papi-types';
import ElementQueries from '@wix/da-shared-react/pkg/ElementQueries';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import CategoryBadgeBox from '../BadgeBox';
// @dynamic-i18n-keys
//   widgets.badges.collections.name.achievements
//   widgets.badges.collections.name.challenge
//   widgets.badges.collections.name.seasonal
//   widgets.badges.collections.name.hidden
//   widgets.badges.collections.name.milestones
//   widgets.badges.collections.description.achievements
//   widgets.badges.collections.description.challenge
//   widgets.badges.collections.description.seasonal
//   widgets.badges.collections.description.hidden
//   widgets.badges.collections.description.milestones
import { useTranslation } from 'react-i18next';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import { BadgeCategoryType } from '../types';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import { URLS } from '@wix/da-url';
import cn from 'classnames';

import s from './BadgeCategory.scss';

export interface Props {
  category: BadgeCategoryType;
  badges: PapiBadge[];
  className?: string;
  profileOwnerUser: PapiUser;
  widthBreakpoints?: number[];
}

const BadgeCategory: FC<Props> = ({
  category,
  badges,
  className,
  widthBreakpoints,
  profileOwnerUser,
}) => {
  const { t } = useTranslation();

  const count = useMemo(() => {
    let num = 0;
    for (let i = 0; i < badges.length; i++) {
      const badge = badges[i];
      if (!badge.isUnearned) {
        num += badge.received || 1;
      }
    }

    if (num > 0) {
      return shortenNumber(num, 0);
    } else {
      return '';
    }
  }, [badges]);

  return (
    <ElementQueries
      className={cn(s['root'], className)}
      widthBreakpoints={widthBreakpoints || [640, 800]}
    >
      <div className={cn(s['headingArea'], category === 'challenge' && s['challengeHeading'])}>
        <Heading className={s['categoryTitle']}>
          <span className={s['title']}>{t(`widgets.badges.collections.name.${category}`)}</span>
          <span className={s['count']}>{count}</span>
        </Heading>
        <p className={s['categoryDescription']}>
          {t(`widgets.badges.collections.description.${category}`)}
        </p>
        {category === 'challenge' && (
          <GhostButton
            size="large"
            variant="main"
            className={s['viewAll']}
            href={URLS.CHALLENGE_BADGES}
          >
            {t('widgets.badges.collections.viewAll')}
          </GhostButton>
        )}
      </div>
      <div className={s['badgeGridArea']}>
        {badges.map((badge, i) => (
          <CategoryBadgeBox
            key={i}
            badge={badge}
            className={s['grid-item']}
            profileOwnerUser={profileOwnerUser}
          />
        ))}
      </div>
    </ElementQueries>
  );
};
BadgeCategory.displayName = 'BadgeCategory';

export default BadgeCategory;
